import { User } from "models/User";
import { OrderResponse } from "models/orderResponse";
import React, { useEffect, useState } from "react";
import { Col, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { AdminService } from "services/AdminService";
import { format } from "date-fns";
import { Puff } from "react-loader-spinner";
import PlatformBadge from "admin/Stores/PlatformBadge";
import { displayOrderAddress, formatTorontoDateTime } from "helpers/orderAddress";
import {
  displayDeliveryNotes,
  displayItemDescription,
  displayOrDash,
  displayPackageNotes,
  isPlatformOrder,
} from "helpers/orderDetails";

const proofImageSrc = (value?: string | null): string => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("data:image") || /^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `data:image/png;base64,${trimmed}`;
};

const PLATFORM_NAMES = ["shopify", "woocommerce", "square", "lightspeed"];

const platformFromOrder = (order?: OrderResponse): string | null => {
  const submission = String(order?.clientSubmissionId || "").trim().toLowerCase();
  const prefix = submission.split(":")[0];
  if (PLATFORM_NAMES.includes(prefix)) {
    return prefix;
  }
  const notes = String(order?.notes || "").trim().toLowerCase();
  const fromNotes = PLATFORM_NAMES.find(name => notes.startsWith(name));
  return fromNotes || null;
};

export interface OrderStatusModalProps {
  isOpen: boolean;
  handleIsOpen: () => void;
  getPDF: (value: string) => void;
  orderId: string;
  isGloble?: boolean;
}

const AdminOrderStatusModal: React.FC<OrderStatusModalProps> = ({
  isOpen,
  handleIsOpen,
  orderId,
  getPDF,
  isGloble,
}: OrderStatusModalProps) => {
  const [order, setOrder] = useState<OrderResponse>();
  const [customer, setCustomer] = useState<User>({} as User);
  const [isloading, setIsloading] = useState<boolean>(true);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    setIsloading(true);
    setOrder(undefined);
    setCustomer({} as User);

    const finishWithCustomer = (userId?: string) => {
      if (!userId) {
        if (!cancelled) {
          setIsloading(false);
        }
        return;
      }
      AdminService.getCustomerById(userId)
        .then(r => {
          if (!cancelled && r.success && r.data) {
            setCustomer(r.data);
          }
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          if (!cancelled) {
            setIsloading(false);
          }
        });
    };

    const request = isGloble
      ? AdminService.getOrderByZuluId(orderId)
      : AdminService.getOrderById(orderId);

    request
      .then(res => {
        if (cancelled) {
          return;
        }
        if (res.success && res.data) {
          setOrder(res.data);
          finishWithCustomer(res.data.userId);
          return;
        }
        setOrder(undefined);
        setIsloading(false);
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) {
          setOrder(undefined);
          setIsloading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, isGloble, orderId]);

  interface TextWithLineBreaksProps {
    text: string;
  }

  const TextWithLineBreaks: React.FC<TextWithLineBreaksProps> = ({ text }) => {
    const maxCharactersPerRow = 30;
    const lines: string[] = [];

    for (let i = 0; i < text.length; i += maxCharactersPerRow) {
      lines.push(text.slice(i, i + maxCharactersPerRow));
    }

    return (
      <div>
        {lines.map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </div>
    );
  };

  const platform = platformFromOrder(order);

  return (
    <React.Fragment>
      <Modal isOpen={isOpen} toggle={handleIsOpen} size="xl" style={{ maxWidth: "1400px" }}>
        <ModalHeader className="order-details-header text-center" toggle={handleIsOpen}>
          <div className="text-center">Order Details : {order?.uniqueId}</div>
          {platform ? (
            <div className="order-details-header__platform">
              <PlatformBadge platform={platform} />
            </div>
          ) : null}
        </ModalHeader>
        {isloading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "500px" }}>
            <Puff color="#FFD0B8" height={50} width={50} />
          </div>
        ) : (
          <>
            {order ? (
              <div>
                <ModalBody className="overflow-auto">
                  <div className="d-flex justify-content-start px-3">
                    <div className="address-wrapper">
                      <div className="row justify-content-center m-3">
                        <Row>
                          <Col lg="6">
                            <Label className="fs-6 text-primary fw-bold">Customer Details</Label>
                            <div>
                              <Row>
                                <Col lg="5" md="5" sm="4" xs="4">
                                  <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                    Company Name
                                  </Label>
                                </Col>
                                <Col>
                                  <Label> : </Label>
                                </Col>
                                <Col lg="5" md="6" sm="6" xs="6">
                                  <Label for="basicpill-companyuin-input9" className="text-muted">
                                    {customer?.role === "business_customer" && customer?.address?.companyName?.trim()
                                      ? customer?.address?.companyName
                                      : [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") || "-"}
                                  </Label>
                                </Col>
                              </Row>
                            </div>
                            <div>
                              <Row>
                                <Col lg="5" md="5" sm="4" xs="4">
                                  <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                    Contact Name
                                  </Label>
                                </Col>
                                <Col>
                                  <Label> : </Label>
                                </Col>
                                <Col lg="5" md="6" sm="6" xs="6">
                                  <Label for="basicpill-companyuin-input9" className="text-muted">
                                    {displayOrDash(
                                      [customer?.firstName, customer?.lastName].filter(Boolean).join(" ")
                                    )}
                                  </Label>
                                </Col>
                              </Row>
                            </div>
                            <div>
                              <Row>
                                <Col lg="5" md="5" sm="4" xs="4">
                                  <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                    Contact Phone
                                  </Label>
                                </Col>
                                <Col>
                                  <Label> : </Label>
                                </Col>
                                <Col lg="5" md="6" sm="6" xs="6">
                                  <Label for="basicpill-companyuin-input9" className="text-muted">
                                    {displayOrDash(customer?.phoneNumber)}
                                  </Label>
                                </Col>
                              </Row>
                            </div>
                            <div>
                              <Row>
                                <Col lg="5" md="5" sm="4" xs="4">
                                  <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                    Email Address
                                  </Label>
                                </Col>
                                <Col>
                                  <Label> : </Label>
                                </Col>
                                <Col lg="5" md="6" sm="6" xs="6">
                                  <Label for="basicpill-companyuin-input9" className="text-muted">
                                    {displayOrDash(customer?.email)}
                                  </Label>
                                </Col>
                              </Row>
                            </div>
                            <div>
                              <Row>
                                <Col lg="5" md="5" sm="4" xs="4">
                                  <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                    Customer Id
                                  </Label>
                                </Col>
                                <Col>
                                  <Label> : </Label>
                                </Col>
                                <Col lg="5" md="6" sm="6" xs="6">
                                  <Label for="basicpill-companyuin-input9" className="text-muted">
                                    {displayOrDash(customer?.customerId)}
                                  </Label>
                                </Col>
                              </Row>
                            </div>
                            <div>
                              <Row>
                                <Col lg="5" md="5" sm="4" xs="4">
                                  <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                    Profile Notes
                                  </Label>
                                </Col>
                                <Col>
                                  <Label> : </Label>
                                </Col>
                                <Col lg="5" md="6" sm="6" xs="6">
                                  <Label for="basicpill-companyuin-input9" className="text-muted">
                                    {customer?.notes?.some(note => String(note.note || "").trim())
                                      ? customer.notes
                                          .filter(note => String(note.note || "").trim())
                                          .map((note, index, notes) => (
                                            <div key={note._id || index}>
                                              {note.note}
                                              {index < notes.length - 1 ? "," : ""}
                                            </div>
                                          ))
                                      : "-"}
                                  </Label>
                                </Col>
                              </Row>
                            </div>
                          </Col>
                          <Col lg="6">
                            <div className="mb-3 form-group">
                              <Label className="fs-6 text-primary fw-bold">Pick-Up Details</Label>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Pickup address
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(displayOrderAddress(order?.pickUpAddress))}
                                      {order?.pickUpAddress?.postalCode?.trim() ? (
                                        <>
                                          <br />
                                          {order.pickUpAddress.postalCode}
                                        </>
                                      ) : null}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Company Name
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.pickUpAddress?.companyName)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Contact Name
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.pickUpAddress?.contactName)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Contact Phone
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.pickUpAddress?.contactPhone)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>

                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Pick - Up date and Time
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {/* {order?.packageReadyTime?.slice(0, -8)} */}
                                      {order?.packageReadyTime && order?.deliveryTime
                                        ? `${formatTorontoDateTime(order.packageReadyTime)} to ${formatTorontoDateTime(order.deliveryTime)}`
                                        : "-"}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Unit No
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.pickUpAddress?.unit)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Created time
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {order?.createdAt ? format(new Date(order.createdAt), "yyyy-MM-dd HH:mm:ss") : "-"}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              {!isPlatformOrder(order) ? (
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Notes about the package
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      <TextWithLineBreaks text={displayPackageNotes(order)} />
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              ) : null}
                                <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Item Description / Size
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      <TextWithLineBreaks text={displayItemDescription(order)} />
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                            </div>
                          </Col>
                        </Row>
                        <Row className="mt-0">
                          <Col lg="6">
                            <div className="mb-3 form-group">
                              <Label className="fs-6 text-primary fw-bold">Delivery Details</Label>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Delivery address
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(displayOrderAddress(order?.deliveryAddress))}
                                      {order?.deliveryAddress?.postalCode?.trim() ? (
                                        <>
                                          <br />
                                          {order.deliveryAddress.postalCode}
                                        </>
                                      ) : null}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Company Name
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.deliveryAddress?.companyName)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Contact Name
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.deliveryAddress?.contactName)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Contact Phone
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.deliveryAddress?.contactPhone)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Total Number of Items
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.totalItems)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Total Weight of all Items
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.totalWeight)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Unit No
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.deliveryAddress?.unit)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Delivery Option
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {order?.deliveryOption === "delivery_verification_pin" && order?.deliveryPin
                                        ?
                                        //  order.deliveryPin
                                         `${order.deliveryOption.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())} (${order.deliveryPin})`
                                        : order?.deliveryOption 
                                          ? order.deliveryOption.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())
                                          : "-"}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Choose Vehicle
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.vehicleType)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              {displayDeliveryNotes(order) && (
                                <div>
                                  <Row>
                                    <Col lg="5" md="5" sm="4" xs="4">
                                      <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                        Notes
                                      </Label>
                                    </Col>
                                    <Col>
                                      <Label> : </Label>
                                    </Col>
                                    <Col lg="5" md="6" sm="6" xs="6">
                                      <Label for="basicpill-companyuin-input9" className="text-muted">
                                        <TextWithLineBreaks text={displayDeliveryNotes(order)} />
                                      </Label>
                                    </Col>
                                  </Row>
                                </div>
                              )}
                              {order?.sentBy && (
                                <div>
                                  <Row>
                                    <Col lg="5" md="5" sm="4" xs="4">
                                      <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                        Sent By
                                      </Label>
                                    </Col>
                                    <Col>
                                      <Label> : </Label>
                                    </Col>
                                    <Col lg="5" md="6" sm="6" xs="6">
                                      <Label for="basicpill-companyuin-input9" className="text-muted">
                                        <TextWithLineBreaks text={order?.sentBy == undefined ? "-" : order?.sentBy} />
                                      </Label>
                                    </Col>
                                  </Row>
                                </div>
                              )}
                            </div>
                          </Col>
                          <Col lg="6">
                            <div className="mb-3 form-group">
                              <Label className="fs-6 text-primary fw-bold">Quote Details</Label>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Total distance
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {displayOrDash(order?.totalDistance)}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Subtotal
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {order?.subTotal == null ? "-" : `$${order.subTotal}`}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                                 {order?.appliedPromo && (
                                                        <div>
                                                          <Row>
                                                            <Col lg="5" md="5" sm="4" xs="4">
                                                              <Label
                                                                for="basicpill-companyuin-input9"
                                                                className="text-muted fw-semibold"
                                                              >
                                                                Promo  Discount applied
                                                              </Label>
                                                            </Col>
                                                            <Col>
                                                              <Label> : </Label>
                                                            </Col>
                                                            <Col lg="5" md="6" sm="6" xs="6">
                                                              <Label
                                                                for="basicpill-companyuin-input9"
                                                                className="text-muted"
                                                              >
                                                                {order?.appliedPromo?.discountPercent?.toString()+ "% "}
                                                              </Label>
                                                            </Col>
                                                          </Row>
                                                        </div>
                                                      )}
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      HST 13%
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {order?.hst == null ? "-" : `$${order.hst}`}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Total Fare
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {order?.totalFare == null ? "-" : `$${order.totalFare.toFixed(2)}`}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                            </div>
                            <div className="mb-3 form-group">
                              <Label className="fs-6 text-primary fw-bold">Order Status</Label>
                              <div>
                                <Row>
                                  <Col lg="5" md="5" sm="4" xs="4">
                                    <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                      Status
                                    </Label>
                                  </Col>
                                  <Col>
                                    <Label> : </Label>
                                  </Col>
                                  <Col lg="5" md="6" sm="6" xs="6">
                                    <Label for="basicpill-companyuin-input9" className="text-muted">
                                      {order?.orderStatus
                                        ? order?.orderStatus.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())
                                        : "Pending"}
                                    </Label>
                                  </Col>
                                </Row>
                              </div>
                              {(order?.orderStatus === "assigned") && (
                                <div>
                                  <Row>
                                    <Col lg="5" md="5" sm="4" xs="4">
                                      <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                        Driver
                                      </Label>
                                    </Col>
                                    <Col>
                                      <Label> : </Label>
                                    </Col>
                                    <Col lg="5" md="6" sm="6" xs="6">
                                      <Label for="basicpill-companyuin-input9" className="text-muted">
                                        {displayOrDash(order?.driverName)}
                                      </Label>
                                    </Col>
                                  </Row>
                                </div>
                              )}
                              {(order?.statusDetails?.status === "picked" || order?.statusDetails?.status === "pick_up_failed") && (
                                <div>
                                  <Row>
                                    <Col lg="5" md="5" sm="4" xs="4">
                                      <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                        Name of shipper
                                      </Label>
                                    </Col>
                                    <Col>
                                      <Label> : </Label>
                                    </Col>
                                    <Col lg="5" md="6" sm="6" xs="6">
                                      <Label for="basicpill-companyuin-input9" className="text-muted">
                                        {displayOrDash(order?.statusDetails?.shipperName)}
                                      </Label>
                                    </Col>
                                  </Row>
                                </div>
                              )}
                              {order?.statusDetails?.status === "delivered" && (
                                <div>
                                  <Row>
                                    <Col lg="5" md="5" sm="4" xs="4">
                                      <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                        Name of receiver
                                      </Label>
                                    </Col>
                                    <Col>
                                      <Label> : </Label>
                                    </Col>
                                    <Col lg="5" md="6" sm="6" xs="6">
                                      <Label for="basicpill-companyuin-input9" className="text-muted">
                                        {displayOrDash(order?.statusDetails?.receiverName)}
                                      </Label>
                                    </Col>
                                  </Row>
                                </div>
                              )}
                              {(order?.statusDetails?.signature ||
                                order?.statusDetails?.deliveredDetails?.deliverSignature) && (
                                <div className="mb-3">
                                  <Row>
                                    <Col lg="5" md="5" sm="4" xs="4">
                                      <Label className="text-muted fw-semibold">Signature</Label>
                                    </Col>
                                    <Col>
                                      <Label>:</Label>
                                    </Col>
                                    <Col lg="5" md="6" sm="6" xs="6">
                                      <div>
                                        <img
                                          src={proofImageSrc(
                                            order.statusDetails.signature ||
                                              order.statusDetails.deliveredDetails?.deliverSignature
                                          )}
                                          alt="Signature"
                                          style={{
                                            maxWidth: "300px",
                                            border: "1px solid #ccc",
                                            borderRadius: "6px",
                                            padding: "4px",
                                          }}
                                        />
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              )}
                              {([
                                "picked",
                                "pick_up_failed",
                                "delivered",
                                "dropped",
                                "return",
                                "returned",
                                "redelivered",
                                "reattempt",
                              ].includes(
                                String(order?.statusDetails?.status || order?.orderStatus || "")
                              )) && (
                                  <div>
                                    {order?.statusDetails?.notes && (
                                      <Row>
                                        <Col lg="5" md="5" sm="4" xs="4">
                                          <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                            Notes
                                          </Label>
                                        </Col>
                                        <Col>
                                          <Label> : </Label>
                                        </Col>
                                        <Col lg="5" md="6" sm="6" xs="6">
                                          <Label for="basicpill-companyuin-input9" className="text-muted">
                                            {displayOrDash(order?.statusDetails?.notes)}
                                          </Label>
                                        </Col>
                                      </Row>
                                    )}
                                    <Row>
                                      <Col lg="5" md="5" sm="4" xs="4">
                                        <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                          Date
                                        </Label>
                                      </Col>
                                      <Col>
                                        <Label> : </Label>
                                      </Col>
                                      <Col lg="5" md="6" sm="6" xs="6">
                                        <Label for="basicpill-companyuin-input9" className="text-muted">
                                          {displayOrDash(order?.statusDetails?.date)}
                                        </Label>
                                      </Col>
                                    </Row>

                                    <Row>
                                      <Col lg="5" md="5" sm="4" xs="4">
                                        <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                          Time
                                        </Label>
                                      </Col>
                                      <Col>
                                        <Label> : </Label>
                                      </Col>
                                      <Col lg="5" md="6" sm="6" xs="6">
                                        <Label for="basicpill-companyuin-input9" className="text-muted">
                                          {displayOrDash(order?.statusDetails?.time)}
                                        </Label>
                                      </Col>
                                    </Row>
                                    <Row>
                                      <Col lg="5" md="5" sm="4" xs="4">
                                        <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                          Driver
                                        </Label>
                                      </Col>
                                      <Col>
                                        <Label> : </Label>
                                      </Col>
                                      <Col lg="5" md="6" sm="6" xs="6">
                                        <Label for="basicpill-companyuin-input9" className="text-muted">
                                          {displayOrDash(order?.driverName)}
                                        </Label>
                                      </Col>
                                    </Row>
                                    {order?.driverNoteAddress && (
                                      <Row>
                                        <Col lg="5" md="5" sm="4" xs="4">
                                          <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                            Driver Location
                                          </Label>
                                        </Col>
                                        <Col>
                                          <Label> : </Label>
                                        </Col>
                                        <Col lg="5" md="6" sm="6" xs="6">
                                          <Label for="basicpill-companyuin-input9" className="text-muted">
                                            {displayOrDash(order?.driverNoteAddress)}
                                          </Label>
                                        </Col>
                                      </Row>
                                    )}
                                  </div>
                                )}
                              {(order?.statusDetails?.status === "pick_up_failed" ||
                                order?.statusDetails?.status === "dropped" ||
                                order?.statusDetails?.status === "delivered" ||
                                order?.statusDetails?.status === "return" ||
                                order?.statusDetails?.status === "reattempt") && (
                                  <div>
                                    <Row>
                                      {/* <Col lg="5" md="5" sm="4" xs="4">
                              <Label for="basicpill-companyuin-input9" className="text-muted fw-semibold">
                                Notes
                              </Label>
                            </Col>
                            <Col>
                              <Label> : </Label>
                            </Col> */}
                                      {order?.statusDetails?.imageData && (
                                        <Col lg="6">
                                          <img className="mt-2" alt="Card cap" src={order?.statusDetails?.imageData} width="100%" />
                                        </Col>
                                      )}
                                    </Row>
                                  </div>
                                )}
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <div className="d-flex flex-row flex-wrap justify-content-end">
                    <button className="btn btn-primary btn-sm px-3" onClick={() => getPDF(order?._id != undefined ? order._id : "")}>
                      Download PDF
                    </button>
                  </div>
                </ModalFooter>
              </div>
            ) : (
              <ModalBody>
                <Row className="justify-content-md-center justify-content-center align-items-center">
                  <Col className="col-md-auto">
                    <div>No data to Show</div>
                  </Col>
                </Row>
              </ModalBody>
            )}
          </>
        )}
      </Modal>
    </React.Fragment>
  );
};

export default AdminOrderStatusModal;
